import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';
import Navigation from '../components/Navigation';
import { X, Calendar, FileText, Users, RefreshCw, Clock, Loader2 } from 'lucide-react';
import TimeTracker from '../components/TimeTracker';
import { toast } from '../lib/toast';

type Task = Database['public']['Tables']['tasks']['Row'];
type TeamMember = Database['public']['Tables']['team_members']['Row'];

interface TaskDetailsProps {
  task: Task;
  teamMembers: TeamMember[];
  onClose: () => void;
}

export default function TaskDetails({ task, teamMembers, onClose }: TaskDetailsProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!task) {
      setError('Task not found');
      return;
    }
    setError(null);
  }, [task]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'Normal': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
      case 'High': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200';
      case 'Urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden relative mx-4">
        {loading && (
          <div className="p-6 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        )}
        {error && (
          <div className="p-6">
            <div className="text-center text-red-600 dark:text-red-400">
              <p>{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
        {!loading && !error && task && (
          <>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{task.title}</h2>
                <div className="relative">
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-4 overflow-y-auto max-h-[calc(90vh-10rem)] pr-2">
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                  {task.due_date && (
                    <span className="flex items-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 px-2.5 py-0.5 rounded-full">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(task.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>

                <div className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto mt-4">
                  <div dangerouslySetInnerHTML={{ __html: task.description || '' }} />
                </div>

                {task.attachment_url && (
                  <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg mt-4">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <a
                      href={task.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 hover:underline"
                    >
                      {task.attachment_name || 'Attachment'}
                    </a>
                  </div>
                )}

                {teamMembers.length > 0 && (
                  <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg mt-4">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {teamMembers.map(m => m.name).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <TimeTracker taskId={task.id} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}